import { ARENA_HEIGHT, ARENA_WIDTH, type Asteroid } from './asteroid-field';
import { getCharacter, type CharacterId } from './characters';

/** What a player is asking for this frame. Opposing directions cancel out. */
export interface MoveIntent {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  boost: boolean;
}

export const NO_INPUT: MoveIntent = {
  up: false,
  down: false,
  left: false,
  right: false,
  boost: false,
};

/** A racer's position and momentum inside the shared arena. */
export interface Body {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Remaining boost, 0..1. */
  boost: number;
}

export const RACER_RADIUS = 26;
const ACCELERATION = 3_000;
const MAX_SPEED = 540;
const BOOST_MULTIPLIER = 1.85;

/** Velocity retained per second. Low enough to feel responsive, not icy. */
const DRAG_PER_SECOND = 0.0009;

const BOOST_DRAIN_PER_SECOND = 0.5;
const BOOST_REFILL_PER_SECOND = 0.28;
/** Below this, the boost will not re-engage — stops a stuttering tap-boost. */
const BOOST_MIN_TO_ENGAGE = 0.08;

/**
 * Racers cannot fly into the right quarter of the arena.
 *
 * Rocks enter from beyond the right edge, so a racer parked there would be hit
 * by something it never had a chance to see. The wall keeps the fight fair.
 */
export const RACER_MAX_X = ARENA_WIDTH * 0.74;

export function createBody(x: number, y: number): Body {
  return { x, y, vx: 0, vy: 0, boost: 1 };
}

export function hitRadiusFor(character: CharacterId): number {
  return RACER_RADIUS * getCharacter(character).size;
}

/**
 * Advances a racer by one step: accelerate along the input, apply drag, spend
 * or refill boost, then move and clamp to the arena.
 *
 * Momentum matters — you cannot stop dead, which is what makes a body-check
 * land and what makes the boost a commitment rather than a free upgrade.
 */
export function advanceBody(
  body: Body,
  intent: MoveIntent,
  deltaMs: number,
  character: CharacterId,
): Body {
  const seconds = deltaMs / 1000;
  const traits = getCharacter(character);

  const boosting = intent.boost && body.boost > BOOST_MIN_TO_ENGAGE;
  const boostLeft = clamp(
    boosting
      ? body.boost - (BOOST_DRAIN_PER_SECOND / traits.boost) * seconds
      : body.boost + BOOST_REFILL_PER_SECOND * seconds,
    0,
    1,
  );

  const dirX = (intent.right ? 1 : 0) - (intent.left ? 1 : 0);
  const dirY = (intent.down ? 1 : 0) - (intent.up ? 1 : 0);
  // Normalise so diagonals are not faster than the cardinals.
  const length = Math.hypot(dirX, dirY) || 1;

  const power = ACCELERATION * traits.speed * (boosting ? BOOST_MULTIPLIER : 1);
  let vx = body.vx + (dirX / length) * power * seconds;
  let vy = body.vy + (dirY / length) * power * seconds;

  // Exponential drag, so the result does not depend on the frame rate.
  const retained = DRAG_PER_SECOND ** seconds;
  vx *= retained;
  vy *= retained;

  const ceiling = MAX_SPEED * traits.speed * (boosting ? BOOST_MULTIPLIER : 1);
  const speed = Math.hypot(vx, vy);
  if (speed > ceiling) {
    vx = (vx / speed) * ceiling;
    vy = (vy / speed) * ceiling;
  }

  const radius = hitRadiusFor(character);
  let x = body.x + vx * seconds;
  let y = body.y + vy * seconds;

  // Stop dead against a wall rather than sliding along it invisibly.
  const clampedX = clamp(x, radius, RACER_MAX_X);
  const clampedY = clamp(y, radius, ARENA_HEIGHT - radius);
  if (clampedX !== x) vx = 0;
  if (clampedY !== y) vy = 0;
  x = clampedX;
  y = clampedY;

  return { x, y, vx, vy, boost: boostLeft };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** How bouncy a body-check is. Above 1 so a shove has real consequence. */
const BUMP_RESTITUTION = 1.35;

/**
 * Resolves two racers overlapping: pushes them apart and trades the component
 * of their velocity along the line between them.
 *
 * Returns the pair unchanged when they are not touching, so callers can apply
 * this every frame.
 */
export function resolveBump(
  a: Body,
  b: Body,
  radiusA: number,
  radiusB: number,
): [Body, Body] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const distance = Math.hypot(dx, dy);
  const reach = radiusA + radiusB;

  if (distance >= reach) {
    return [a, b];
  }

  // Perfectly stacked racers have no line to push along; pick one.
  const nx = distance === 0 ? 1 : dx / distance;
  const ny = distance === 0 ? 0 : dy / distance;

  // Separate them so they are just touching, splitting the correction evenly.
  const overlap = (reach - distance) / 2;
  const pushedA = { ...a, x: a.x - nx * overlap, y: a.y - ny * overlap };
  const pushedB = { ...b, x: b.x + nx * overlap, y: b.y + ny * overlap };

  // Exchange the velocity along the normal; sideways motion is untouched.
  const alongA = a.vx * nx + a.vy * ny;
  const alongB = b.vx * nx + b.vy * ny;
  if (alongA - alongB <= 0) {
    // Already separating — separate them, but do not add energy.
    return [pushedA, pushedB];
  }

  const exchange = (alongA - alongB) * BUMP_RESTITUTION;
  return [
    { ...pushedA, vx: a.vx - exchange * nx, vy: a.vy - exchange * ny },
    { ...pushedB, vx: b.vx + exchange * nx, vy: b.vy + exchange * ny },
  ];
}

/** How close a rock must pass to count as a near miss, beyond the hit radius. */
export const NEAR_MISS_BAND = 38;

export function distanceToAsteroid(x: number, y: number, asteroid: Asteroid): number {
  return Math.hypot(x - asteroid.x, y - asteroid.y) - asteroid.radius;
}

/**
 * Circle overlap between a racer and a rock.
 *
 * The hit circle is a little smaller than the sprite — near misses should feel
 * survivable rather than cheap.
 */
export function hitsAsteroid(
  x: number,
  y: number,
  asteroid: Asteroid,
  racerRadius: number,
): boolean {
  return distanceToAsteroid(x, y, asteroid) < racerRadius * 0.82;
}

/** The first rock the racer is touching, or null when the arena is clear. */
export function firstCollision(
  x: number,
  y: number,
  racerRadius: number,
  asteroids: readonly Asteroid[],
): Asteroid | null {
  return asteroids.find((asteroid) => hitsAsteroid(x, y, asteroid, racerRadius)) ?? null;
}

/**
 * True when a rock slipped past close enough to be worth points but not close
 * enough to kill. Callers fire this once per rock, as it crosses the racer.
 */
export function isNearMiss(
  x: number,
  y: number,
  asteroid: Asteroid,
  racerRadius: number,
): boolean {
  const gap = distanceToAsteroid(x, y, asteroid);
  return gap >= racerRadius * 0.82 && gap <= racerRadius * 0.82 + NEAR_MISS_BAND;
}

export { ARENA_HEIGHT, ARENA_WIDTH };
